// The one Stytch client instance. `createStytchClient` is the headless client
// (`createStytchUIClient` is the other one) — no prebuilt Stytch UI ever
// renders; the login/signup pages are ours.
//
// Imported from the package root rather than `@stytch/react/headless` because
// tsconfig pins `moduleResolution: "node"`, which cannot see `exports`
// subpaths. No loss: `StytchProvider` and the hooks only exist on the root
// entry, so the graph is identical either way.
//
// Built on first use, not at import: the constructor posts a telemetry event
// and starts the session-refresh timer, and a test or story that only wants a
// type from this module should not pay for either.
import { createStytchClient } from "@stytch/react";

import { sessionCookieOptions } from "./shared-session";

export { SESSION_DURATION_MINUTES } from "./shared-session";

export type StytchClient = ReturnType<typeof createStytchClient>;

let instance: StytchClient | null = null;

/** The parent domain the session cookie is shared on (shared-session.ts); unset on localhost. */
export const COOKIE_DOMAIN = process.env.REACT_APP_AUTH_COOKIE_DOMAIN || undefined;

/** The marketing site, when it holds the sign-in page: `/login` and `/signup` forward there
    (SignInOnSite). Unset, as in local dev and tests, this app signs people in itself. */
export const SIGN_IN_SITE = process.env.REACT_APP_SITE_URL || null;

/** The app's client. Memoised, so every caller observes the same session. */
export const getStytch = (): StytchClient =>
  (instance ??= createStytchClient(process.env.REACT_APP_STYTCH_PUBLIC_TOKEN ?? "", {
    cookieOptions: sessionCookieOptions(COOKIE_DOMAIN),
  }));

// Stytch sends the browser back here with `?stytch_token_type=…&token=…` for
// both OAuth and magic links, so login and signup share one redirect URL.
export const callbackUrl = () => `${window.location.origin}/auth/callback`;
